<template>
  <v-container fluid>
    <v-card>
      <v-toolbar color="secondary" flat dense>
        <v-toolbar-title>Sales Report</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn small text to="/sales-summary">
          <v-icon left>mdi-keyboard-backspace</v-icon>Back
        </v-btn>
      </v-toolbar>

      <v-card-text>
        <div>
          <v-row dense>
            <!-- Date From -->
            <v-col cols="12" xs="12" md="3">
              <v-menu v-model="menu1" :close-on-content-click="false" :nudge-right="40" transition="scale-transition"
                offset-y min-width="290px">
                <template v-slot:activator="{ on }">
                  <v-text-field dense outlined single-line v-model="record.StartDate" label="From"
                    prepend-icon="mdi-calendar-month" v-on="on" readonly></v-text-field>
                </template>
                <v-date-picker v-model="record.StartDate" @input="menu1 = false"></v-date-picker>
              </v-menu>
            </v-col>

            <!-- Date To -->
            <v-col cols="12" xs="12" md="3">
              <v-menu v-model="menu2" :close-on-content-click="false" :nudge-right="40" transition="scale-transition"
                offset-y min-width="290px">
                <template v-slot:activator="{ on }">
                  <v-text-field dense outlined single-line v-model="record.EndDate" label="To"
                    prepend-icon="mdi-calendar-month" v-on="on" readonly></v-text-field>
                </template>
                <v-date-picker v-model="record.EndDate" @input="menu2 = false"></v-date-picker>
              </v-menu>
            </v-col>

            <!-- Category Filter -->
            <v-col cols="12" xs="12" md="3">
              <v-autocomplete
                name="categoryFilter"
                dense
                outlined
                v-model="record.CategoryId"
                :items="categories"
                label="Select Category (Optional)"
                item-text="Name"
                item-value="id"
                clearable
              ></v-autocomplete>
            </v-col>

            <!-- Brand Filter -->
            <v-col cols="12" xs="12" md="3">
              <v-autocomplete
                name="brandFilter"
                dense
                outlined
                v-model="record.BrandId"
                :items="brands"
                label="Select Brand (Optional)"
                item-text="Name"
                item-value="id"
                clearable
              ></v-autocomplete>
            </v-col>

            <!-- Generate Button -->
            <v-col cols="12" xs="12" md="3">
              <v-btn @click="getReport" color="primary" :loading="loader">
                <v-icon left>mdi-chart-multiple</v-icon>Generate Report
              </v-btn>
            </v-col>
          </v-row>

          <v-divider class="mb-4"></v-divider>

          <div v-if="reportData.length > 0">
            <v-row dense class="subtitle-2 text--primary">
              <v-col cols="12">
                <v-text-field label="Search" v-model="search" class="mb-4"></v-text-field>
              </v-col>

              <v-col cols="12" xs="12" md="4">
                <span class="totals">Total Selling Price: {{ TotalSellingPrice | currency }}</span><br>
                <span class="totals">Total Cost of Sales: {{ TotalBuyingPrice | currency }}</span><br>
                <span class="totals">Total Profit: {{ TotalProfit | currency }}</span><br>
                <span class="totals">Profit Margin: {{ profitMarginPercentage }}%</span><br>

                <div class="d-flex align-center mt-2" style="gap:12px">
                  <download-excel :data="reportData" :fields="fields" name="combined-profit-report">
                    <v-icon color="green">mdi-file-excel</v-icon>
                  </download-excel>
                  <v-btn icon small :loading="pdfLoader" :disabled="!reportData.length" @click="generatePdf" title="Download PDF">
                    <v-icon color="red">mdi-file-pdf-box</v-icon>
                  </v-btn>
                </div>
              </v-col>

              <v-col cols="12">
                <v-data-table 
                  :items-per-page="25" 
                  :headers="headers" 
                  :items="reportData"
                  :search="search"
                  :loading="loader"
                  loading-text="Loading profit data..."
                  class="font-weight-medium"
                >
                  <template v-slot:item.BuyingPrice="{ item }">
                    {{ item.BuyingPrice | currency }}
                  </template>

                  <template v-slot:item.SellingPrice="{ item }">
                    {{ item.SellingPrice | currency }}
                  </template>

                  <template v-slot:item.LineTotal="{ item }">
                    {{ item.LineTotal | currency }}
                  </template>

                  <template v-slot:item.Date="{ item }">
                    {{ item.Date | date }}
                  </template>

                  <template v-slot:no-data>
                    <v-alert :value="true" color="info" icon="mdi-information">
                      No profit data found for the selected criteria.
                    </v-alert>
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
          </div>

          <div v-else-if="!loader">
            <v-alert type="info" class="mt-4">
              <v-icon left>mdi-information</v-icon>
              Select date range and click "Generate Report" to load combined profit data from all stores. 
              Optionally select a category to filter results.
            </v-alert>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <pdf-preview ref="pdfPreview"></pdf-preview>
    <!-- snackbar -->
    <snackbar ref="snackbar"></snackbar>
  </v-container>
</template>

<script>
import { http, host } from "@/common/index";
export default {
  data() {
    return {
      record: {
        StartDate: null,
        EndDate: null,
        CategoryId: null,
        BrandId: null
      },
      menu1: false,
      menu2: false,
      search: null,
      loader: false,
      pdfLoader: false,
      categories: [],
      brands: [],
      reportData: [],
      TotalProfit: 0,
      TotalSellingPrice: 0,
      TotalBuyingPrice: 0,
      headers: [
        { text: "Product", value: "Product" },
        { text: "Qty Sold", value: "Quantity" },
        { text: "Cost of Sales", value: "BuyingPrice" },
        { text: "Selling Price", value: "SellingPrice" },
        { text: "Total Sales", value: "LineTotal" },
        { text: "Date", value: "Date" },
      ],
      fields: {
        Product: "Product",
        Quantity: "Quantity",
        CostOfSales: "BuyingPrice",
        SellingPrice: "SellingPrice",
        TotalSales: "LineTotal",
        Date: "Date",
      },
    };
  },
  computed: {
    profitMarginPercentage() {
      if (this.TotalSellingPrice === 0) return '0.00';
      return ((this.TotalProfit / this.TotalSellingPrice) * 100).toFixed(2);
    }
  },
  methods: {
    getReport() {
      if (!this.record.StartDate || !this.record.EndDate) {
        this.$refs.snackbar.show("Please select both start and end dates", "red");
        return;
      }

      const data = { ...this.record };
      const url = "/combined-profit-report";
      const self = this;
      
      self.loader = true;
      self.reportData = [];
      self.TotalProfit = 0;
      self.TotalSellingPrice = 0;
      self.TotalBuyingPrice = 0;

      this.$store
        .dispatch("post", { url, data })
        .then((res) => {
          console.log(res, "combined profit report");
          if (res.ResultCode == 1200) {
            self.reportData = res.ResponseData.sales || [];
            self.TotalProfit = res.ResponseData.totalProfit || 0;
            self.TotalSellingPrice = res.ResponseData.totalSellingPrice || 0;
            self.TotalBuyingPrice = res.ResponseData.totalBuyingPrice || 0;
          } else {
            this.$refs.snackbar.show(res.msg || res.ResultDesc, "red");
          }
        })
        .catch((err) => {
          this.$refs.snackbar.show(err, "red");
        })
        .finally(() => {
          self.loader = false;
        });
    },
    generatePdf() {
      if (!this.record.StartDate || !this.record.EndDate) {
        this.$refs.snackbar.show("Please select both start and end dates", "red");
        return;
      }
      const self = this;
      this.pdfLoader = true;
      http
        .post(`${host}/combined-profit-report-pdf`, this.record, { responseType: "blob" })
        .then((response) => {
          const blob = new Blob([response.data], { type: "application/pdf" });
          const fileUrl = URL.createObjectURL(blob);
          self.$refs.pdfPreview.show(fileUrl);
        })
        .catch((err) => {
          self.$refs.snackbar.show(err, "red");
        })
        .finally(() => {
          self.pdfLoader = false;
        });
    },
    getCategories() {
      const self = this;
      this.$store
        .dispatch("get", `/products-categories`)
        .then((res) => {
          if (res.ResultCode == 1200) {
            self.categories = res.ResponseData || [];
          } else {
            this.$refs.snackbar.show(res.msg, "red");
          }
        })
        .catch((err) => {
          this.$refs.snackbar.show(err, "red");
        });
    },
    getBrands() {
      const self = this;
      this.$store
        .dispatch("get", `/brands`)
        .then((res) => {
          if (res.ResultCode == 1200) {
            self.brands = res.ResponseData || [];
          } else {
            this.$refs.snackbar.show(res.msg, "red");
          }
        })
        .catch((err) => {
          this.$refs.snackbar.show(err, "red");
        });
    }
  },
  created() {
    this.getCategories();
    this.getBrands();
  }
};
</script>

<style lang="scss" scoped>
.totals {
  font-weight: 500;
  font-size: 16px;
  line-height: 1.6;
}
</style>